import { useState } from "react";
import { useConfig } from "wagmi";
import { getPublicClient, getWalletClient, waitForTransactionReceipt } from "wagmi/actions";
import { parseUnits } from "viem";
import { keelVaultAbi } from "../abi";
import { esEstable, fraccionASwapear } from "../lib/precio";
import type { RedKeel } from "../config/networks";
import type { EstadoVault } from "./useVault";

const POOL_ABI = [
  // slot0 truncado a (sqrtPriceX96, tick): Slipstream devuelve 6 campos, Uniswap 7; con 2 vale para ambos
  { type: "function", name: "slot0", stateMutability: "view", inputs: [],
    outputs: [{ type: "uint160" }, { type: "int24" }] },
  { type: "function", name: "observe", stateMutability: "view", inputs: [{ type: "uint32[]" }],
    outputs: [{ type: "int56[]" }, { type: "uint160[]" }] },
  { type: "function", name: "liquidity", stateMutability: "view", inputs: [],
    outputs: [{ type: "uint128" }] },
] as const;

/**
 * Abrir la posición de un vault que YA EXISTE y ya tiene el dinero dentro.
 *
 * POR QUE HACE FALTA. Hasta hoy la única forma de abrir una posición era el flujo de creación
 * completo, que siempre crea un vault NUEVO. Si la apertura fallaba —y en una pool fina falla, por
 * el deslizamiento— el usuario se quedaba con el dinero dentro de un vault sin posición y sin
 * ninguna pantalla desde la que reintentar: la única salida era retirar y volver a empezar, con
 * otro vault nuevo y otras cinco firmas. Pasó cuatro veces seguidas el 2026-08-17 y dejó cuatro
 * vaults vacíos por el camino.
 *
 * El deslizamiento se calcula igual que en `useCrear` y por el mismo motivo: el swap de apertura
 * mueve el precio de las pools finas él solo, y el mínimo de salida se mide contra el TWAP, así que
 * hay que cubrir también la brecha entre spot y TWAP — en su peor caso, que es
 * `maxTwapDeviationTicks`, porque por encima de eso `_requireHonestPrice` rechaza la operación
 * entera.
 */
export function useAbrir(red: RedKeel | undefined) {
  const config = useConfig();
  const [haciendo, setHaciendo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function abrir(v: EstadoVault, anchoPct: number, gasTexto: string) {
    if (!red) return false;
    setError(null);
    try {
      const publico = getPublicClient(config, { chainId: red.idCadena });
      const wallet = await getWalletClient(config, { chainId: red.idCadena });
      if (!publico || !wallet) throw new Error("sin cliente RPC o wallet");

      // ---- 1. quién es el estable, y con qué token se trabaja
      // Cuál de los dos es el estable. Si ninguno lo parece —un par volátil/volátil— se respeta lo
      // que el vault ya tenga: `recoverToken0` es la unidad en la que está medido `costBasisRef`, y
      // cambiarlo a mitad de vida haría que la recuperación persiguiera un objetivo en otra moneda.
      const refEsToken0 = esEstable(v.token0.symbol)
        ? true
        : esEstable(v.token1.symbol)
          ? false
          : v.refEsToken0;

      // El capital es lo que YA hay suelto en el vault. Se abre con el token que tenga saldo; si
      // hay de los dos, manda el estable, que es la unidad en la que se mide todo lo demas.
      const libre0 = parseUnits(String(v.libre.a0), v.token0.decimals);
      const libre1 = parseUnits(String(v.libre.a1), v.token1.decimals);
      const depEsToken0 = refEsToken0 ? libre0 > 0n : !(libre1 > 0n);
      const dep = depEsToken0 ? v.token0 : v.token1;
      const saldo = depEsToken0 ? libre0 : libre1;
      const gasWei = parseUnits(gasTexto || "0", dep.decimals);
      if (saldo <= gasWei) {
        throw new Error(
          `El vault tiene ${v.libre.a0 || v.libre.a1} ${dep.symbol} sueltos y la bolsa de gas que ` +
            `pides es de ${gasTexto}. Deja algo para la posición.`
        );
      }
      const desplegable = saldo - gasWei;

      // ---- 2. el rango y cuánto hay que vender para entrar en él
      setHaciendo("calculando");
      const semiancho =
        Math.max(1, Math.round(Math.log(1 + anchoPct / 100) / Math.log(1.0001) / v.spacing)) * v.spacing;

      const [slot0, L] = await Promise.all([
        publico.readContract({ address: v.pool, abi: POOL_ABI, functionName: "slot0" }),
        publico.readContract({ address: v.pool, abi: POOL_ABI, functionName: "liquidity" }),
      ]);
      const twap = await publico
        .readContract({ address: v.pool, abi: POOL_ABI, functionName: "observe", args: [[300, 0]] })
        .then((r) => {
          const d = r[0][1]! - r[0][0]!;
          let a = Number(d / 300n);
          if (d < 0n && d % 300n !== 0n) a -= 1;
          return a;
        })
        .catch(() => null);
      const centro = twap ?? slot0[1];

      // El flujo "terminar de abrir" recupera un vault con rango simétrico ya guardado, así que los
      // dos semianchos son el mismo (no hay forma asimétrica que persistir aquí).
      const frac = fraccionASwapear(slot0[0], centro, v.spacing, semiancho, semiancho, depEsToken0);
      const swapWei = (desplegable * BigInt(Math.round(frac * 1e6))) / 1_000_000n;

      // ---- 3. el deslizamiento que esta pool pide de verdad
      let bps = 0;
      if (swapWei > 0n && L > 0n) {
        const Q96 = 2n ** 96n;
        const sq = slot0[0];
        const sqFin = depEsToken0
          ? (L * Q96 * sq) / (L * Q96 + swapWei * sq)
          : sq + (swapWei * Q96) / L;
        const r = Number(sqFin) / Number(sq);
        const mov = Math.abs(r * r - 1);
        const brechaMax = Math.abs(Math.pow(1.0001, v.maxTwapDeviationTicks) - 1);
        bps = Math.ceil(((mov / 2) * 1.5 + brechaMax) * 10000) + 25;
      }
      if (bps > 1000) {
        throw new Error(
          `Con ese capital, el swap de apertura movería el precio de esta pool más de lo que ningún ` +
            `deslizamiento razonable cubre. Prueba con un rango más ancho, que necesita vender menos.`
        );
      }

      // ---- 4. subirlo si hace falta, ANTES de abrir
      if (bps > v.swapSlippageBps) {
        setHaciendo("ajustando el deslizamiento");
        const h = await wallet.writeContract({
          address: v.direccion, abi: keelVaultAbi, functionName: "setGuards",
          args: [v.twapWindow, v.maxTwapDeviationTicks, bps, v.cooldown],
        });
        const rec = await waitForTransactionReceipt(config, { hash: h, chainId: red.idCadena });
        if (rec.status === "reverted") throw new Error("El ajuste del deslizamiento revirtió.");
      }

      // ---- 5. abrir
      setHaciendo("abriendo la posición");
      const h = await wallet.writeContract({
        address: v.direccion,
        abi: keelVaultAbi,
        functionName: "open",
        args: [
          depEsToken0,
          swapWei,
          semiancho,
          refEsToken0,
          0n,                                  // sin reserva: la reinyeccion se configura aparte
          depEsToken0 ? gasWei : 0n,
          depEsToken0 ? 0n : gasWei,
        ],
      });
      const rec = await waitForTransactionReceipt(config, { hash: h, chainId: red.idCadena });
      if (rec.status === "reverted") {
        throw new Error(
          `La apertura entró en el bloque pero revirtió. El dinero sigue dentro del vault y puedes ` +
            `volver a intentarlo — o retirarlo, que eso no depende del precio.`
        );
      }
      return true;
    } catch (e) {
      setError(e instanceof Error ? (e.message.split("\n")[0] ?? e.message) : String(e));
      return false;
    } finally {
      setHaciendo(null);
    }
  }

  return { abrir, haciendo, error };
}
